// @flow
import type { BillingAddress } from 'api/shared';
import { STRIPE_ERROR_CODES_MESSAGE_MAP } from 'utils/constants';
import { postJson } from 'utils/http';
import { waitTaskCompleted } from 'utils/asyncBackendTask';

export type UTMParams = {
  utmSource?: string,
  utmMedium?: string,
  utmCampaign?: string,
  utmTerm?: string,
  utmContent?: string
};

export type CreditCard = {
  cvc: string,
  expMonth: string,
  expYear: string,
  methodId: string,
  number: string
};

export type Donation = {
  ...$Exact<BillingAddress>,
  ...$Exact<UTMParams>,
  sfCampaignId?: string,
  amount: string,
  otherAmount: string,
  firstName: string,
  gift: boolean,
  lastName: string,
  email: string,
  phone: string,
  sendEmails: true,
  note: string
};

export type DonationPayload = {
  ...$Exact<Donation>,
  ...$Exact<CreditCard>
};

const getFieldError = ({ code, param: name }) => ({
  code,
  name,
  description: STRIPE_ERROR_CODES_MESSAGE_MAP[code]
});

const getTokenFromStripe = (cardData: CreditCard) =>
  new Promise((resolve, reject) =>
    Stripe.card.createToken(cardData, (status, response) => {
      if (status === 200) {
        resolve(response.id);
      } else {
        // Handle the case where there is no credit card data submitted
        const generalMessage =
          response.error.type === 'invalid_request_error'
            ? STRIPE_ERROR_CODES_MESSAGE_MAP.invalid_number
            : STRIPE_ERROR_CODES_MESSAGE_MAP[response.error.code];

        const fieldError: { name: string, description: string } =
          response.error.type === 'invalid_request_error'
            ? {
                name: 'number',
                description: STRIPE_ERROR_CODES_MESSAGE_MAP.invalid_number
              }
            : getFieldError(response.error);

        // eslint-disable-next-line prefer-promise-reject-errors
        reject({
          responseJson: {
            errors: [
              fieldError,
              { name: 'general', description: generalMessage }
            ]
          }
        });
      }
    })
  );

const createDonation = (donation, token) =>
  postJson(`/data/website/donate`, {
    ...donation,
    token
  }).then(({ task }) => task);

const handleStripeBackgroundTask = (task: BackgroundTask) =>
  waitTaskCompleted(task.url);

export const makeDonation = (values: DonationPayload) => {
  const { cvc, expMonth, expYear, methodId, number, ...donation } = values;
  return getTokenFromStripe({ cvc, expMonth, expYear, methodId, number })
    .then(token => createDonation(donation, token))
    .then(task => handleStripeBackgroundTask(task))
    .then(result => result);
};
